import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Mail, Settings, Save, Loader2, Send, RefreshCw, FileText, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TEMPLATE_LABELS = {
  registration: "Registrazione",
  booking_confirmed: "Prenotazione Confermata",
  booking_cancelled: "Prenotazione Annullata",
  payment_success: "Pagamento Ricevuto",
  payment_failed: "Pagamento Fallito",
  subscription_expiring: "Abbonamento in Scadenza",
  welcome: "Benvenuto"
};

export default function AdminEmails() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("sendgrid");
  
  // SendGrid State
  const [emailSettings, setEmailSettings] = useState({
    from_email: "",
    from_name: "NoleggioFurgoni.it",
    is_configured: false,
    api_key_configured: false
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [emailVerificationEnabled, setEmailVerificationEnabled] = useState(false);
  const [togglingVerification, setTogglingVerification] = useState(false);
  
  // Templates State
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ subject: "", body_html: "" });
  const [templateSaving, setTemplateSaving] = useState(false);

  useEffect(() => {
    if (token) {
      fetchEmailSettings();
      fetchTemplates();
    }
  }, [token]);

  const fetchEmailSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/email/smtp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSettings({
        from_email: res.data.from_email || "",
        from_name: res.data.from_name || "NoleggioFurgoni.it",
        is_configured: res.data.is_configured || false,
        api_key_configured: res.data.api_key_configured || false
      });
      setEmailVerificationEnabled(res.data.email_verification_enabled || false);
    } catch (error) {
      toast.error("Errore nel caricamento impostazioni email");
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleEmailVerification = async () => {
    setTogglingVerification(true);
    try {
      const newValue = !emailVerificationEnabled;
      await axios.put(
        `${API}/admin/email/verification-toggle`,
        { enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailVerificationEnabled(newValue);
      toast.success(newValue ? "Verifica email abilitata" : "Verifica email disabilitata");
    } catch (error) {
      toast.error("Errore nell'aggiornamento impostazione");
    } finally {
      setTogglingVerification(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/email/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento template");
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/admin/email/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSendingTest(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      subject: template.subject,
      body_html: template.body_html
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.subject || !templateForm.body_html) {
      toast.error("Compila tutti i campi");
      return;
    }

    setTemplateSaving(true);
    try {
      await axios.put(
        `${API}/admin/email/templates/${editingTemplate.template_type}`,
        templateForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Template salvato");
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleResetTemplate = async (templateType) => {
    if (!window.confirm("Ripristinare il template ai valori predefiniti?")) return;
    
    try {
      await axios.post(
        `${API}/admin/email/templates/${templateType}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Template ripristinato");
      fetchTemplates();
    } catch (error) {
      toast.error("Errore nel ripristino");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Gestione <span className="text-primary">Email</span>
          </h1>
          <p className="text-sm text-muted-foreground">Configura SendGrid e personalizza i template email</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="sendgrid" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Impostazioni SendGrid
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Template Email
            </TabsTrigger>
          </TabsList>

          {/* SendGrid Settings */}
          <TabsContent value="sendgrid">
            <div className="card-dashboard">
              {settingsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <h2 className="font-bold">Configurazione SendGrid API</h2>
                    {emailSettings.is_configured ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle2 className="h-4 w-4" /> Configurato
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-yellow-600 text-sm">
                        <XCircle className="h-4 w-4" /> Non configurato
                      </span>
                    )}
                  </div>

                  {/* Status Card */}
                  <div className={`p-4 rounded-lg border ${emailSettings.is_configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                    <div className="flex items-start gap-3">
                      {emailSettings.is_configured ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      )}
                      <div>
                        <h3 className={`font-medium ${emailSettings.is_configured ? 'text-green-800' : 'text-yellow-800'}`}>
                          {emailSettings.is_configured ? 'SendGrid Configurato' : 'SendGrid Non Configurato'}
                        </h3>
                        {emailSettings.is_configured ? (
                          <div className="text-sm text-green-700 mt-1 space-y-1">
                            <p><strong>Email mittente:</strong> {emailSettings.from_email}</p>
                            <p><strong>Nome mittente:</strong> {emailSettings.from_name}</p>
                            <p><strong>API Key:</strong> Configurata via variabili d'ambiente</p>
                          </div>
                        ) : (
                          <p className="text-sm text-yellow-700 mt-1">
                            Configura le seguenti variabili d'ambiente nel tuo hosting (Railway, Vercel, ecc.):
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {!emailSettings.is_configured && (
                      <div className="mt-4 p-3 bg-white/80 rounded border border-yellow-200 font-mono text-xs">
                        <p className="text-gray-600 mb-2">Variabili d'ambiente richieste:</p>
                        <code className="block">SENDGRID_API_KEY=SG.xxxxxx</code>
                        <code className="block">MAIL_FROM=noreply@tuodominio.it</code>
                        <code className="block">MAIL_FROM_NAME=NoleggioFurgoni.it</code>
                        <code className="block">SITE_URL=https://tuodominio.it</code>
                      </div>
                    )}
                  </div>

                  {/* Email Verification Toggle */}
                  <div className="p-4 bg-muted/50 rounded-lg border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">Verifica Email alla Registrazione</h3>
                        <p className="text-sm text-muted-foreground">
                          Se abilitato, i nuovi utenti devono verificare la loro email con un codice prima di completare la registrazione.
                        </p>
                      </div>
                      <Button
                        variant={emailVerificationEnabled ? "default" : "outline"}
                        size="sm"
                        onClick={toggleEmailVerification}
                        disabled={togglingVerification || !emailSettings.is_configured}
                        className={emailVerificationEnabled ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {togglingVerification ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : emailVerificationEnabled ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Abilitato
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 mr-2" />
                            Disabilitato
                          </>
                        )}
                      </Button>
                    </div>
                    {!emailSettings.is_configured && (
                      <p className="text-xs text-yellow-600 mt-2">
                        ⚠️ Configura prima SendGrid per abilitare la verifica email
                      </p>
                    )}
                  </div>

                  {/* Test Email Button */}
                  {emailSettings.is_configured && (
                    <div className="flex gap-3 pt-4 border-t border-border">
                      <Button variant="outline" onClick={handleSendTest} disabled={sendingTest}>
                        {sendingTest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        Invia Email di Test
                      </Button>
                      <p className="text-sm text-muted-foreground self-center">
                        L'email verrà inviata al tuo indirizzo admin
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Email Templates */}
          <TabsContent value="templates">
            <div className="card-dashboard">
              {templatesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary" />
                    <h2 className="font-bold">Template Email</h2>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">
                    Personalizza i template delle email automatiche. Usa le variabili tra doppie parentesi graffe
                    (es. {"{{name}}"}) che verranno sostituite con i dati reali.
                  </p>

                  <div className="space-y-3">
                    {templates.map((template) => (
                      <div
                        key={template.template_type}
                        className="flex items-center justify-between p-4 border border-border hover:border-primary/50 transition-colors"
                      >
                        <div>
                          <h3 className="font-medium">{TEMPLATE_LABELS[template.template_type] || template.template_type}</h3>
                          <p className="text-sm text-muted-foreground">{template.subject}</p>
                          {template.updated_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Modificato: {new Date(template.updated_at).toLocaleDateString("it-IT")}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEditTemplate(template)}>
                            Modifica
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetTemplate(template.template_type)}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Template Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Modifica Template: {editingTemplate && (TEMPLATE_LABELS[editingTemplate.template_type] || editingTemplate.template_type)}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="input-label">Oggetto Email</label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="Oggetto dell'email"
              />
            </div>

            <div>
              <label className="input-label">Corpo Email (HTML)</label>
              <Textarea
                value={templateForm.body_html}
                onChange={(e) => setTemplateForm({ ...templateForm, body_html: e.target.value })}
                placeholder="<h1>Titolo</h1><p>Contenuto...</p>"
                className="font-mono text-sm min-h-[300px]"
              />
            </div>

            <div className="bg-muted/50 p-3 rounded text-sm">
              <p className="font-medium mb-2">Variabili disponibili:</p>
              <code className="text-xs">
                {"{{name}}, {{email}}, {{booking_id}}, {{vehicle_name}}, {{start_date}}, {{end_date}}, {{total_price}}, {{plan}}, {{amount}}, {{expiry_date}}"}
              </code>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>Annulla</Button>
            <Button onClick={handleSaveTemplate} disabled={templateSaving} className="btn-primary">
              {templateSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salva Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
